import { useState, useCallback } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { useSubscription } from '@/contexts/billing/subscription';
import { SubscriptionService } from '@/services/billing/SubscriptionService';

const SYNC_COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes

export const useBillingSync = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
  const { invalidateAndRefetch } = useSubscription();
  const { refreshProfile } = useAuth();

  const canSync = useCallback(() => {
    if (!lastSyncTime) return true;
    return Date.now() - lastSyncTime >= SYNC_COOLDOWN_MS;
  }, [lastSyncTime]);

  const getTimeUntilNextSync = useCallback(() => {
    if (!lastSyncTime) return 0;
    const timeRemaining = (lastSyncTime + SYNC_COOLDOWN_MS) - Date.now();
    return Math.max(0, Math.ceil(timeRemaining / (60 * 1000))); // Return minutes remaining
  }, [lastSyncTime]);

  const syncBilling = useCallback(async () => {
    if (!canSync()) {
      const minutesRemaining = getTimeUntilNextSync();
      const error = `Please wait ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} before syncing again`;
      setError(error);
      throw new Error(error);
    }

    setLoading(true);
    setError(null);

    try {
      const result = await SubscriptionService.syncBillingData();
      setLastSyncTime(Date.now());
      invalidateAndRefetch();
      await refreshProfile();
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [canSync, getTimeUntilNextSync, invalidateAndRefetch, refreshProfile]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    syncBilling,
    loading,
    error,
    canSync: canSync(),
    minutesUntilNextSync: getTimeUntilNextSync(),
    clearError,
  };
};
